import React, { useCallback, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents/strings';
import { setSelectedBulletinId } from '@/store/slices/residents/homeSlice';
import { useNotifications, NotificationItem } from '@/hooks/common/useNotifications';
import { useMarketplaceFeedbackModal } from '@/hooks/common/useMarketplaceFeedbackModal';
import { NotificationList } from '@/components/residents/notification/NotificationList';
import { NotificationDetails } from '@/components/residents/notification/NotificationDetails';
import { LeaveFeedbackModal } from '@/components/residents/marketplace';
import { logger } from '@/utils/common/logger';

export const DesktopNotificationsScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const selectedId = useAppSelector((s) => s.home.selectedBulletinId || null);
  const { notifications, isLoading, formatDateMMMddyyyy, isMarketplaceFeedback, isCorkBoardWarning, markAsRead, reload } = useNotifications();
  
  const feedbackModal = useMarketplaceFeedbackModal({ onSuccess: reload });

  const onPressRow = useCallback(async (b: NotificationItem) => {
    if (isMarketplaceFeedback(b)) {
      feedbackModal.openModal(b);
      return;
    }

    if (isCorkBoardWarning(b)) {
      try { await markAsRead(b); } catch (e) { logger.warn('notificationCorkBoardMarkReadFailed', e); }
      const threadId = b.corkBoardNotification?.metadata?.threadId;
      if (threadId) {
        navigation.navigate('ThreadDetail', { threadId: String(threadId) });
      }
      return;
    }

    try {
      await markAsRead(b);
    } catch (e) {
      logger.warn('notificationMarkReadFailed', e);
    }
    dispatch(setSelectedBulletinId(b.id));
  }, [dispatch, isMarketplaceFeedback, isCorkBoardWarning, markAsRead, feedbackModal, navigation]);

  const renderRight = useCallback((item: NotificationItem) => null, []);

  const selected = useMemo(() => (notifications || []).find(n => n.id === selectedId && !isMarketplaceFeedback(n)) || null, [notifications, selectedId, isMarketplaceFeedback]);

  return (
    <View style={styles.root}>
      <View style={styles.container}>
        <View style={[styles.leftPane, { borderRightColor: colors.card.border, backgroundColor: colors.card.bg }]}>
          <View style={[styles.header, { borderBottomColor: colors.separator }]}>
            <Text style={[styles.headerTitle, { color: colors.text.primary }]}>{strings.notifications.desktop.title}</Text>
          </View>
          <NotificationList
            data={notifications}
            isLoading={isLoading}
            emptyText={strings.notifications.desktop.empty}
            onPressItem={onPressRow}
            showDateSmall
            dateFor={(b) => formatDateMMMddyyyy(b.createdAt)}
            selectedId={selectedId}
            renderRight={renderRight}
            rowVariant={'screen'}
          />
        </View>
        <View style={styles.rightPane}>
          {!selected ? (
            <View style={styles.placeholder}>
              <Text style={[styles.placeholderTitle, { color: colors.text.primary }]}>{strings.notifications.desktop.placeholder.title}</Text>
              <Text style={[styles.placeholderSub, { color: colors.text.subtle }]}>{strings.notifications.desktop.placeholder.subtitle}</Text>
            </View>
          ) : (
            <NotificationDetails selected={selected} />
          )}
        </View>
      </View>
      
      <LeaveFeedbackModal
        visible={feedbackModal.isVisible}
        feedbackRequest={feedbackModal.feedbackRequest}
        onClose={feedbackModal.closeModal}
        onSubmit={feedbackModal.submitFeedback}
        isSubmitting={feedbackModal.isSubmitting}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  leftPane: {
    width: 440,
    borderRightWidth: 1,
  },
  rightPane: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  header: {
    paddingHorizontal: REDESIGN_SPACING.horizontalPadding,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  headerTitle: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
  },
  placeholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  placeholderTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
  },
  placeholderSub: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
});

export default DesktopNotificationsScreen;
